/**
 * use-wip-thresholds.ts
 *
 * Aged WIP threshold settings hook — PS-11, PS-12, PS-13.
 *
 * Reads agedWipDaysThreshold, agedWipAmountWarning, and agedWipAmountAlert
 * from the company billing-settings endpoint and exposes them for use in
 * the Billing settings tab and in WIP-consuming components.
 */

import { useState, useCallback, useEffect } from 'react';
import { getCompanyBillingSettings, patchCompanyBillingSettings } from '../lib/data-service';

// ── Defaults (match current hardcoded values across the codebase) ─────────────

export const WIP_THRESHOLD_DEFAULTS = {
  agedWipDaysThreshold: 60,
  agedWipAmountWarning: 30_000,
  agedWipAmountAlert: 50_000,
} as const;

// ── Types ─────────────────────────────────────────────────────────────────────

export interface WipThresholds {
  agedWipDaysThreshold: number;
  agedWipAmountWarning: number;
  agedWipAmountAlert: number;
}

export interface UseWipThresholdsReturn {
  thresholds: WipThresholds;
  hasUnsavedChanges: boolean;
  isSaving: boolean;
  isLoading: boolean;
  updateDaysThreshold: (value: number) => void;
  updateAmountWarning: (value: number) => void;
  updateAmountAlert: (value: number) => void;
  save: () => Promise<void>;
  discard: () => void;
}

// ── Module-level cache (shared with consumer components) ──────────────────────

let _cachedThresholds: WipThresholds = { ...WIP_THRESHOLD_DEFAULTS };

// ── Hook ──────────────────────────────────────────────────────────────────────

export function useWipThresholds(): UseWipThresholdsReturn {
  const [saved, setSaved] = useState<WipThresholds>(_cachedThresholds);
  const [current, setCurrent] = useState<WipThresholds>(_cachedThresholds);
  const [isSaving, setIsSaving] = useState(false);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;

    setIsLoading(true);
    getCompanyBillingSettings()
      .then((response) => {
        if (cancelled) return;
        const thresholds: WipThresholds = {
          agedWipDaysThreshold: response.agedWipDaysThreshold ?? WIP_THRESHOLD_DEFAULTS.agedWipDaysThreshold,
          agedWipAmountWarning: response.agedWipAmountWarning ?? WIP_THRESHOLD_DEFAULTS.agedWipAmountWarning,
          agedWipAmountAlert: response.agedWipAmountAlert ?? WIP_THRESHOLD_DEFAULTS.agedWipAmountAlert,
        };
        _cachedThresholds = thresholds;
        setSaved(thresholds);
        setCurrent(thresholds);
      })
      .catch((err) => {
        if (cancelled) return;
        console.warn('[WipThresholds] Failed to fetch billing settings, using defaults', err);
        setSaved({ ...WIP_THRESHOLD_DEFAULTS });
        setCurrent({ ...WIP_THRESHOLD_DEFAULTS });
      })
      .finally(() => {
        if (!cancelled) setIsLoading(false);
      });

    return () => { cancelled = true; };
  }, []);

  const hasUnsavedChanges =
    current.agedWipDaysThreshold !== saved.agedWipDaysThreshold ||
    current.agedWipAmountWarning !== saved.agedWipAmountWarning ||
    current.agedWipAmountAlert !== saved.agedWipAmountAlert;

  const updateDaysThreshold = useCallback((value: number) => {
    setCurrent((prev) => ({ ...prev, agedWipDaysThreshold: value }));
  }, []);

  const updateAmountWarning = useCallback((value: number) => {
    setCurrent((prev) => ({ ...prev, agedWipAmountWarning: value }));
  }, []);

  const updateAmountAlert = useCallback((value: number) => {
    setCurrent((prev) => ({ ...prev, agedWipAmountAlert: value }));
  }, []);

  const save = useCallback(async () => {
    setIsSaving(true);
    try {
      await patchCompanyBillingSettings({
        agedWipDaysThreshold: current.agedWipDaysThreshold,
        agedWipAmountWarning: current.agedWipAmountWarning,
        agedWipAmountAlert: current.agedWipAmountAlert,
      });
      _cachedThresholds = { ...current };
      setSaved({ ...current });
    } finally {
      setIsSaving(false);
    }
  }, [current]);

  const discard = useCallback(() => {
    setCurrent(saved);
  }, [saved]);

  return {
    thresholds: current,
    hasUnsavedChanges,
    isSaving,
    isLoading,
    updateDaysThreshold,
    updateAmountWarning,
    updateAmountAlert,
    save,
    discard,
  };
}

// ── Standalone reader (for use outside React components) ──────────────────────

/**
 * Returns the current WIP thresholds from the module-level cache.
 * Falls back to WIP_THRESHOLD_DEFAULTS if the hook has not yet mounted.
 * Consumer components (WIP report, dashboards, notifications) should call
 * this after the settings page has initialised the cache, or use the hook
 * directly if they need live reactivity.
 */
export function getWipThresholds(): WipThresholds {
  return { ..._cachedThresholds };
}
